"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";

interface TocItem {
  id: string;
  text: string;
  level: number;
}

interface TableOfContentsProps {
  variant?: "inline" | "sidebar";
  contentsLabel: string;
  introLabel: string;
}

function gatherHeadings(): TocItem[] {
  const article = document.querySelector("article");
  if (!article) return [];

  const headings = article.querySelectorAll("h2, h3");
  const tocItems: TocItem[] = [];

  headings.forEach((heading) => {
    const tocLabel =
      heading.getAttribute("data-toc-label")?.trim() ||
      heading.textContent?.trim() ||
      "";

    if (!heading.id) {
      heading.id =
        tocLabel
          .toLowerCase()
          .replaceAll(/[^a-z0-9]+/g, "-")
          .replaceAll(/(^-|-$)/g, "") ?? "";
    }
    if (heading.id && tocLabel) {
      tocItems.push({
        id: heading.id,
        text: tocLabel,
        level: heading.tagName === "H2" ? 2 : 3,
      });
    }
  });

  return tocItems.length >= 3 ? tocItems : [];
}

export default function TableOfContents({
  variant = "inline",
  contentsLabel,
  introLabel,
}: Readonly<TableOfContentsProps>) {
  const [items, setItems] = useState<TocItem[]>([]);
  const [activeId, setActiveId] = useState<string>("");

  useEffect(() => {
    let settled = false;

    const tryGather = () => {
      if (settled) return;
      const headings = gatherHeadings();
      if (headings.length > 0) {
        settled = true;
        setItems(headings);
      }
    };

    const observer = new MutationObserver(tryGather);
    observer.observe(document.body, { childList: true, subtree: true });

    tryGather();

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    // Only the sticky sidebar TOC stays in view while scrolling, so it is the
    // only variant that needs active-section tracking. Gating here avoids
    // attaching duplicate scroll/resize listeners when both the inline and
    // sidebar TOCs are rendered (one is merely CSS-hidden) on redesigned posts.
    if (items.length === 0 || variant !== "sidebar") return;

    const elements: HTMLElement[] = items
      .map((i) => document.getElementById(i.id))
      .filter((el): el is HTMLElement => el !== null);

    if (elements.length === 0) return;

    // Reading line near the top of the viewport (below the sticky header).
    const ACTIVE_OFFSET = 120;

    const computeActive = () => {
      let current = "";
      for (const el of elements) {
        if (el.getBoundingClientRect().top - ACTIVE_OFFSET <= 0) {
          current = el.id;
        } else {
          break;
        }
      }
      setActiveId((prev) => (prev === current ? prev : current));
    };

    let ticking = false;
    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        computeActive();
        ticking = false;
      });
    };

    computeActive();
    globalThis.addEventListener("scroll", onScroll, { passive: true });
    globalThis.addEventListener("resize", onScroll);
    return () => {
      globalThis.removeEventListener("scroll", onScroll);
      globalThis.removeEventListener("resize", onScroll);
    };
  }, [items, variant]);

  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!activeId || variant !== "sidebar") return;
    const el = scrollRef.current;
    const activeEl = el?.querySelector(`[data-toc-id="${activeId}"]`);
    if (el && activeEl && typeof el.scrollBy === "function") {
      const containerRect = el.getBoundingClientRect();
      const activeRect = activeEl.getBoundingClientRect();
      const offset =
        activeRect.top -
        containerRect.top -
        el.clientHeight / 2 +
        activeRect.height / 2;
      el.scrollBy({ top: offset, behavior: "smooth" });
    }
  }, [activeId, variant]);

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
      e.preventDefault();
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
        setActiveId(id);
        history.replaceState(null, "", `#${id}`);
      }
    },
    [],
  );

  const handleScrollToTop = useCallback(() => {
    globalThis.scrollTo({ top: 0, behavior: "smooth" });
    setActiveId("");
    history.replaceState(
      null,
      "",
      `${globalThis.location.pathname}${globalThis.location.search}`,
    );
  }, []);

  if (items.length === 0) return null;

  const sidebarItemClass = (isActive: boolean) =>
    clsx(
      "block py-0.5 leading-relaxed transition-colors font-monument text-sm",
      isActive
        ? "font-medium text-gail-primary"
        : "text-black hover:text-gail-secondary",
    );

  if (variant === "sidebar") {
    return (
      <nav aria-label="Table of contents" className="not-prose">
        <div className="rounded-2xl border border-black/10 bg-white p-5">
          <p className="mb-3 text-xs uppercase tracking-wide font-monument font-medium text-gail-gray">
            {contentsLabel}
          </p>
          <div
            ref={scrollRef}
            className="max-h-[320px] overflow-y-auto [&::-webkit-scrollbar]:w-1 [&::-webkit-scrollbar-track]:bg-transparent [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-gray-200 hover:[&::-webkit-scrollbar-thumb]:bg-gray-300"
          >
            <ol className="flex flex-col gap-1">
              <li>
                <button
                  type="button"
                  data-toc-id="__intro"
                  onClick={handleScrollToTop}
                  className={clsx(
                    "cursor-pointer text-left",
                    sidebarItemClass(activeId === ""),
                  )}
                >
                  {introLabel}
                </button>
              </li>
              {items.map((item) => (
                <li key={item.id} className={item.level === 3 ? "ml-3" : ""}>
                  <a
                    href={`#${item.id}`}
                    data-toc-id={item.id}
                    onClick={(e) => handleClick(e, item.id)}
                    className={sidebarItemClass(activeId === item.id)}
                  >
                    {item.text}
                  </a>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </nav>
    );
  }

  const inlineItemClass = (isActive: boolean) =>
    clsx(
      "text-sm transition-colors",
      isActive
        ? "font-monument font-medium text-gail-primary"
        : "font-monument text-black hover:text-gail-secondary",
    );

  return (
    <nav
      aria-label="Table of contents"
      className="not-prose mb-8 xl:hidden rounded-2xl border border-black/5 bg-white p-5"
    >
      <p className="mb-3 uppercase tracking-wide font-monument text-xs font-medium text-gail-gray">
        {contentsLabel}
      </p>
      <ol className="flex flex-col gap-1.5">
        <li>
          <button
            type="button"
            onClick={handleScrollToTop}
            className={clsx(
              "cursor-pointer text-left",
              inlineItemClass(activeId === ""),
            )}
          >
            {introLabel}
          </button>
        </li>
        {items.map((item) => (
          <li key={item.id} className={item.level === 3 ? "ml-4" : ""}>
            <a
              href={`#${item.id}`}
              onClick={(e) => handleClick(e, item.id)}
              className={inlineItemClass(activeId === item.id)}
            >
              {item.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}
